import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { api } from '@/api/client';
import { useAuth } from '@/store/auth';
import { X, KeyRound } from 'lucide-react';

export function CambiarPasswordModal({ onClose }: { onClose: () => void }) {
  const markChanged = useAuth((s) => s.markPasswordChanged);
  const [actual, setActual] = useState('');
  const [nueva, setNueva] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [ok, setOk] = useState(false);

  const mut = useMutation({
    mutationFn: async () => {
      const { data } = await api.post('/auth/cambiar-password', {
        password_actual: actual,
        password_nuevo: nueva,
      });
      return data;
    },
    onSuccess: () => {
      setOk(true);
      markChanged();
      setTimeout(onClose, 1500);
    },
    onError: (e: any) => setError(e?.response?.data?.detail || 'Error al cambiar contraseña'),
  });

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (nueva.length < 6) {
      setError('La nueva contraseña debe tener al menos 6 caracteres');
      return;
    }
    if (nueva !== confirm) {
      setError('La confirmación no coincide');
      return;
    }
    if (nueva.toUpperCase() === 'ERM2026') {
      setError('No puedes reusar la contraseña temporal');
      return;
    }
    mut.mutate();
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-2xl w-full max-w-md">
        <header className="flex items-center justify-between px-5 py-3 border-b">
          <div className="flex items-center gap-2 text-arpp-primary font-semibold">
            <KeyRound size={18} /> Cambiar contraseña
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700">
            <X size={20} />
          </button>
        </header>
        <form onSubmit={onSubmit} className="p-5 space-y-4">
          {ok ? (
            <div className="bg-green-50 border border-green-200 text-green-800 text-sm rounded p-3">
              ✅ Contraseña actualizada. Usa la nueva en tus próximos ingresos.
            </div>
          ) : (
            <>
              <div>
                <label className="block text-xs text-gray-700 mb-1">Contraseña actual</label>
                <input
                  type="password"
                  value={actual}
                  onChange={(e) => setActual(e.target.value)}
                  required
                  autoFocus
                  className="w-full border rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-arpp-primary"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-700 mb-1">Contraseña nueva (mín. 6)</label>
                <input
                  type="password"
                  value={nueva}
                  onChange={(e) => setNueva(e.target.value)}
                  required
                  className="w-full border rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-arpp-primary"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-700 mb-1">Confirmar nueva</label>
                <input
                  type="password"
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  required
                  className="w-full border rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-arpp-primary"
                />
              </div>
              {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 text-xs rounded p-2">
                  {error}
                </div>
              )}
              <div className="flex justify-end gap-2 pt-2">
                <button
                  type="button"
                  onClick={onClose}
                  className="px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={mut.isPending}
                  className="px-4 py-2 text-sm bg-arpp-primary text-white rounded hover:bg-blue-800 disabled:opacity-50"
                >
                  {mut.isPending ? 'Guardando…' : 'Cambiar contraseña'}
                </button>
              </div>
            </>
          )}
        </form>
      </div>
    </div>
  );
}
